<template>
  <div>
    <PageHeaderBanner>
      <template #title>お知らせ一覧</template>
    </PageHeaderBanner>
    <MessageChip :display="isMessageChip" level="E" :message="errMessage" />
    <div class="cmd01000-result-table">
      <div
        :class="
          'chem-table-area btn-exist table-top-button ' + (rows.length === 0 ? 'table-nodata' : '')
        "
      >
        <q-table
          class="my-sticky-header-column-table"
          :rows="rows"
          :columns="columns"
          row-key="index"
          flat
          dense
          separator="cell"
          :hide-no-data="hideNoDataFlg"
          no-data-label="データがありません。"
          :rows-per-page-options="[10, 20, 50, 0]"
          rows-per-page-label="件表示"
          rows-per-page="20"
          :pagination-label="
            (firstRowIndex, endRowIndex, totalRowsNumber) => {
              return `${totalRowsNumber} 件中 ${firstRowIndex}～${endRowIndex} 件表示`;
            }
          "
        >
          <template #no-data="{ message }">
            <div class="full-width row q-gutter-sm">
              <q-icon size="24px" name="mdi-alert-circle-outline" />
              <span class="q-pt-xs">{{ message }}</span>
            </div>
          </template>
          <template #top>
            <div class="add-btn-area flex items-center filter-select">
              <q-btn icon="mdi-plus" class="add-btn" color="primary" @click="registerNews"
                >追加</q-btn
              >
            </div>
          </template>

          <!-- 編集削除Header -->
          <template #header-cell-editDelete>
            <q-th class="table-btn-area delete-btn-exists"></q-th>
          </template>

          <!-- トップ・ログインlabel用Body -->
          <template #body-cell-label="cell">
            <q-td v-if="!hideNoDataFlg" class="table-btn-area label-area">
              <div
                :class="
                  'row items-center ' +
                  (cell.row.browseable_from_login_page && cell.row.browseable_from_top_page
                    ? 'justify-center'
                    : '')
                "
              >
                <label
                  :class="
                    (cell.row.browseable_from_login_page ? 'login-label' : 'white-label') +
                    ' q-mr-sm'
                  "
                  ><span v-if="cell.row.browseable_from_login_page">ログイン</span></label
                >
                <label :class="cell.row.browseable_from_top_page ? 'top-label' : 'white-label'">
                  <span v-if="cell.row.browseable_from_top_page">トップ</span></label
                >
              </div>
            </q-td>
          </template>

          <!-- 公開中label用Body -->
          <template #body-cell-publication_period_date="publish">
            <q-td v-if="!hideNoDataFlg" class="label-publish">
              <div
                :class="'row items-center ' + (publish.row.now_publishing ? 'justify-center' : '')"
              >
                <p class="q-mb-none">{{ publish.row.publication_period_date }}</p>
                <label
                  :class="publish.row.now_publishing ? 'publishing-label q-ml-sm' : 'white-label'"
                >
                  <span v-if="publish.row.now_publishing">公開中</span></label
                >
              </div>
            </q-td>
          </template>

          <!-- 編集、削除Body -->
          <template #body-cell-editDelete="cell">
            <q-td v-if="!hideNoDataFlg" class="table-btn-area delete-btn-exists">
              <div class="flex">
                <q-btn icon="mdi-pencil" color="primary" @click="editNews(cell.row.news_id)">
                  編集
                </q-btn>
                <q-btn
                  label="削除"
                  icon="mdi-close"
                  class="deletebtn"
                  color="primary"
                  outline
                  style="color: var(--q-delete)"
                  @click="deleteConfDialog(cell.row.news_id, cell.row.news_title)"
                />
              </div>
            </q-td>
          </template>
        </q-table>
        <AlertDialog
          :display="dialogDisplayFlg"
          :level="DIALOG_LEVEL"
          :confirmation="dialogConfFlg"
          :title="dialogTitle"
          :text="dialogText"
          @complete="dialogComplete()"
          @confirm="deleteNews()"
          @close="dialogDisplayFlg = false"
        />
      </div>
    </div>
  </div>
</template>
<script setup lang="ts">
import { ref } from 'vue';
import { useRouter } from 'vue-router';
import { Loading } from 'quasar';
import DetailResultStatusCode from '../../common/code/DetailResultStatusCode';
import ResultStatusCode from '../../common/code/ResultStatusCode';
import axios from '../../common/axios-common';

import PageHeaderBanner from '../../components/banner/PageHeaderBanner.vue';
import MessageChip from '../../components/chip/MessageChip.vue';
import AlertDialog from '../../components/dialogs/AlertDialog.vue';
import HttpResponseStatusCode from '../../common/code/HttpResponseStatusCode';

const router = useRouter();
// 削除ボタンが押されたお知らせID
const deleteNewsId = ref('');
// ダイアログに表示するお知らせタイトル
const dialogNewsTitle = ref('');
// ダイアログのdisplayFlg(表示判断)
const dialogDisplayFlg = ref(false);
// ダイアログのlevel(informationで固定)
const DIALOG_LEVEL = 'information';
// ダイアログのtitle
const dialogTitle = ref('');
// ダイアログのtext(配列にすることでXSSの対策を行う)
const dialogText = ref(['']);
// ダイアログのconfirmation(trueならキャンセルボタンを表示しない)
const dialogConfFlg = ref(false);
// エラーメッセージ表示フラグ
const isMessageChip = ref(false);
// エラーメッセージ
const errMessage = ref('');
// 初期表示読み込み時「データがありません」を非表示にするためのフラグ(falseで表示)
const hideNoDataFlg = ref(true);

interface MNews {
  news_id: string;
  publish_date: string;
  publication_period_date: string;
  news_title: string;
  browseable_from_login_page: boolean;
  browseable_from_top_page: boolean;
  create_user_name: string;
  create_timestamp: string;
  update_user_name: string;
  update_timestamp: string;
  now_publishing: boolean;
}

// カラム
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const columns = ref<any>([
  {
    name: 'editDelete',
    label: '',
  },
  {
    name: 'publish_date',
    label: '公開開始日時',
    align: 'left',
    field: (row: MNews) => row.publish_date,
    sortable: true,
    style: 'width: 170px',
  },
  {
    name: 'publication_period_date',
    label: '公開終了日時',
    align: 'left',
    field: (row: MNews) => row.publication_period_date,
    sortable: true,
    headerClasses: 'label-publish',
  },
  {
    name: 'news_title',
    label: '件名',
    align: 'left',
    field: (row: MNews) => row.news_title,
    sortable: true,
  },
  {
    name: 'label',
    label: '表示対象',
    align: 'left',
    field: (row: MNews) => row.now_publishing,
    headerClasses: 'label-area',
  },
  {
    name: 'create_user_name',
    label: '登録者',
    align: 'left',
    field: (row: MNews) => row.create_user_name,
    sortable: true,
  },
  {
    name: 'create_timestamp',
    label: '登録日時',
    align: 'left',
    field: (row: MNews) => row.create_timestamp,
    sortable: true,
    style: 'width: 170px',
  },
  {
    name: 'update_user_name',
    label: '更新者',
    align: 'left',
    field: (row: MNews) => row.update_user_name,
    sortable: true,
  },
  {
    name: 'update_timestamp',
    label: '更新日時',
    align: 'left',
    field: (row: MNews) => row.update_timestamp,
    sortable: true,
    style: 'width: 170px',
  },
]);

const rows = ref([
  {
    news_id: '',
    publish_date: '',
    publication_period_date: '',
    news_title: '',
    browseable_from_login_page: false,
    browseable_from_top_page: false,
    create_user_name: '',
    create_timestamp: '',
    update_user_name: '',
    update_timestamp: '',
    now_publishing: false,
  },
]);

/**
 * エラー初期化
 */
const resetErrors = () => {
  errMessage.value = '';
  isMessageChip.value = false;
};

// お知らせ新規登録に遷移
const registerNews = () => {
  router.push({ name: 'newsRegister', params: { mode: '0' } });
};

// お知らせ情報変更に遷移
const editNews = (newsId: string) => {
  router.push({ name: 'newsEdit', params: { mode: '1', newsId } });
};

// お知らせ一覧取得処理
const viewNews = async () => {
  resetErrors();
  // Loading Start
  Loading.show();

  // お知らせ一覧を取得するController呼び出し
  await axios
    .post('/cmd010001/view')
    .then((res) => {
      const { common, newsList } = res.data;

      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          rows.value = newsList;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          // エラー画面へ遷移
          router.push({ name: 'error' });
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他のエラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
      hideNoDataFlg.value = false;
    });
};

// 削除確認ダイアログのOKボタン押下時の処理
const deleteNews = async () => {
  resetErrors();
  // Loading Start
  Loading.show();
  // 削除するお知らせIDをformDataへ
  const formData = new FormData();
  formData.append('news_id', deleteNewsId.value);

  // お知らせ削除処理
  await axios
    .post('/cmd010001/delete', formData)
    .then((res) => {
      const { common } = res.data;
      switch (common.status) {
        // 正常終了の場合
        case ResultStatusCode.SUCCESS:
          // 削除確認ダイアログのtitleを設定
          dialogTitle.value = '削除完了';
          // 削除確認ダイアログのtextを生成
          dialogText.value = [`お知らせを削除しました。`];
          // ダイアログのボタン数判断をtrue(1つ)に
          dialogConfFlg.value = true;
          break;
        // エラーの場合
        case ResultStatusCode.ERROR:
          switch (common.detail_status) {
            case DetailResultStatusCode.UPDATE_ERROR:
              errMessage.value = common.message;
              isMessageChip.value = true;
              // ダイアログ表示判断をfalse(非表示)に変更
              dialogDisplayFlg.value = false;
              break;
            default:
          }
          break;
        default:
          router.push({ name: 'error' });
      }
    })
    .catch((err) => {
      if (err.response.status === HttpResponseStatusCode.SESSIONTIMEOUT) {
        // セッションタイムアウトの場合
        router.push({ name: 'login', query: { timeout: 'timeout' } });
      } else {
        // その他のエラーの場合
        router.push({ name: 'error' });
      }
    })
    .finally(() => {
      // Loading End
      Loading.hide();
    });
};

// 削除確認のダイアログを表示
const deleteConfDialog = (newsId: string, title: string) => {
  deleteNewsId.value = newsId;
  dialogNewsTitle.value = title;

  // 削除確認ダイアログのtitleを設定
  dialogTitle.value = '削除確認';
  // 削除確認ダイアログのtextを生成
  dialogText.value = [`${dialogNewsTitle.value} を削除します。`, `よろしいですか？`];
  // ダイアログのボタン数判断をfalse(2つ)に
  dialogConfFlg.value = false;
  // ダイアログ表示判断をtrue(表示)に変更
  dialogDisplayFlg.value = true;
};

// 削除完了ダイアログのOKボタン押下時の処理
const dialogComplete = async () => {
  // ダイアログ表示判断をfalse(非表示)に変更
  dialogDisplayFlg.value = false;
  // 一覧を再度表示
  viewNews();
};

// 初期表示処理呼び出し
viewNews();
</script>
<style scoped>
.cmd01000-result-table {
  width: 90%;
  margin: 0 auto;
}
.cmd01000-result-table td label {
  background-color: var(--q-primary);
  border-radius: 5px;
  padding: 6px 0 3px;
  text-align: center;
  color: white;
  width: 70px;
  font-weight: bold;
}
.cmd01000-result-table td label.top-label {
  background-color: var(--q-gradientEnd);
}
.cmd01000-result-table td label.white-label {
  background-color: white;
}
.cmd01000-result-table td label.publishing-label {
  background-color: var(--q-informationPublish);
  padding: 3px 0;
  margin-bottom: 2px;
}
</style>
<style>
.cmd01000-result-table .chem-table-area.btn-exist .label-publish {
  min-width: 215px;
  width: 215px;
  padding: 4px 10px;
  margin: 0;
  padding: 4px 10px;
}

.cmd01000-result-table .label-area {
  min-width: 170px;
  width: 170px;
}
</style>
